import { useQuery } from '@apollo/client';
import { Loader } from '@volumental/common';
import { type JSX, useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { graphql } from '../../../api/gql';
import { GeneralErrorPage } from '../../error/GeneralErrorPage';
import { PaperFormat } from '../../../api/gql/graphql';
import { OnboardingImagePage } from './onboarding-image-page';
import { PaperSizeCard } from './paper-size-card';

const GetOnboardingPaperSizeSelectConfigurationQuery = graphql(`
  query getOnboardingPaperSizeSelectConfigurationQuery {
    configuration {
      scanner {
        supportedReferenceObjects
      }
    }
  }
`);

interface OnboardingPaperSizeSelectProps {
  onClick: () => void;
  onClose: () => void;
  imageSrc: string;
  buttonText: string;
  imageNumber: number;
  imageCount: number;
  altText: string;
}

export function OnboardingPaperSizeSelect({
  onClick,
  onClose,
  imageSrc,
  buttonText,
  imageCount,
  imageNumber,
  altText,
}: OnboardingPaperSizeSelectProps): JSX.Element {
  const { loading, error, data } = useQuery(
    GetOnboardingPaperSizeSelectConfigurationQuery,
  );
  const [paperSelector, setPaperSelector] = useState(false);
  const { t } = useTranslation();

  const onNext = (): void => {
    setPaperSelector(false);
    onClick();
  };

  if (loading) {
    return <Loader />;
  }
  if (error || !data) {
    return (
      <GeneralErrorPage
        errorText="Missing OnboardingPaperSizeSelectProps configuration"
        onClose={onClose}
      />
    );
  }

  const { supportedReferenceObjects } = data.configuration.scanner;
  const preferredReferenceObject =
    supportedReferenceObjects.length === 1
      ? supportedReferenceObjects[0]
      : undefined;

  const paperTitlesForCountry = (): JSX.Element => {
    return (
      <Trans>
        {t('onboarding.getWhitePaper', {
          paper:
            preferredReferenceObject === PaperFormat.A4
              ? t('paperSize.A4OrUS')
              : t('paperSize.USOrA4'),
        })}
      </Trans>
    );
  };

  return (
    <>
      <OnboardingImagePage
        instruction={paperTitlesForCountry()}
        imageSrc={imageSrc}
        imageNumber={imageNumber}
        imageCount={imageCount}
        buttonText={buttonText}
        altText={altText}
        onClick={() => {
          setPaperSelector(true);
        }}
      />
      <PaperSizeCard
        show={paperSelector}
        onReady={onNext}
        onClose={() => {
          setPaperSelector(false);
        }}
        preferredReferenceObject={preferredReferenceObject}
      />
    </>
  );
}
